import { Heading, Alert } from '@restworld/ui-ds-base';
import { EditButton } from './card-wrapper';
import { AcademicCapIcon, UserAcademicCapIcon, toDateFormat, useModal } from '@restworld/utils-common';
import { BookOpenIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { WorkerCertificate } from '@restworld/utility-types';
import { PresentationCard } from '@restworld/ui-components';
import { useMemo } from 'react';
import { ProfileEditModal } from './profile-edit';
import { animated, useSpring } from '@react-spring/web';
import { useTranslation } from 'next-i18next';

export const CertificationSectionItem = ({
  workerCertificate,
  workerCertificateIndex,
}: {
  workerCertificate: WorkerCertificate;
  workerCertificateIndex: number;
}) => {
  const { t } = useTranslation();
  const { show: showModal, open: openModal, close: closeModal } = useModal();

  const memoValues = useMemo(() => {
    const certificateType = workerCertificate?.certificate_type_var.label;
    const courseName = workerCertificate?.course_name;
    const corporateName = workerCertificate?.corporate_name;
    const releaseDate = workerCertificate?.date ? toDateFormat(workerCertificate?.date) : undefined;
    return {
      certificateType,
      courseName,
      corporateName,
      releaseDate,
    };
  }, [
    workerCertificate?.certificate_type_var.label,
    workerCertificate?.corporate_name,
    workerCertificate?.course_name,
    workerCertificate?.date,
  ]);

  const { certificateType, courseName, corporateName, releaseDate } = memoValues;
  const isInformationMissing = !certificateType || !courseName || !corporateName || !releaseDate;

  const viewContentStyles = useSpring({
    opacity: !showModal ? 1 : 0,
    transform: !showModal ? 'translateY(0%)' : 'translateY(5px)',
    config: { tension: 250, friction: 20 },
  });

  const editContentStyles = useSpring({
    opacity: showModal ? 1 : 0,
    transform: showModal ? 'translateY(0%)' : 'translateY(5px)',
    config: { tension: 250, friction: 20 },
  });

  const errorMessage = t('common:missing_info');

  return (
    <div className="flex flex-col w-full">
      {!showModal && (
        <div className="flex flex-row justify-between  items-center w-full mb-4">
          <Heading size={5} weight="medium">
            {t('common:certificate_1')} {workerCertificateIndex + 1}
          </Heading>
          <EditButton
            onClick={() => {
              openModal();
            }}
          />
        </div>
      )}
      {isInformationMissing && (
        <animated.div style={viewContentStyles} className="w-full">
          <Alert text={t('onboarding-page:missing_info_fill_fields')} className="mb-4" />
        </animated.div>
      )}
      {!showModal && (
        <animated.div style={viewContentStyles} className="flex lg:flex-row flex-col lg:gap-8 gap-4 w-full">
          <div className="flex flex-col lg:w-1/2 w-full items-start justify-start gap-4">
            <PresentationCard
              label={t('onboarding-page:type_of_course')}
              header={certificateType || errorMessage}
              icon={<AcademicCapIcon />}
              isError={!certificateType}
            />
            <PresentationCard
              label={t('onboarding-page:course_name')}
              header={courseName || errorMessage}
              isError={!courseName}
              icon={<BookOpenIcon className="w-6 h-6" />}
            />
          </div>
          <div className="flex flex-col lg:w-1/2 w-full items-start justify-start gap-4">
            <PresentationCard
              label={t('common:release_date')}
              header={releaseDate || errorMessage}
              isError={!releaseDate}
              icon={<UserAcademicCapIcon />}
            />
            <PresentationCard
              label={t('onboarding-page:issue_organization')}
              header={corporateName || errorMessage}
              isError={!corporateName}
              icon={<BuildingOfficeIcon className="w-6 h-6" />}
            />
          </div>
        </animated.div>
      )}
      {showModal && (
        <animated.div style={editContentStyles} className="w-full">
          <ProfileEditModal
            showModal={showModal}
            onClose={closeModal}
            editPageType="certifications"
            mode="edit"
            editPageTitle={`${t('common:certificate_1')} ${workerCertificateIndex + 1}`}
            key={`certificato-${workerCertificateIndex + 1}`}
            editIndex={workerCertificateIndex}
          />
        </animated.div>
      )}
    </div>
  );
};
